// Member (agent) session for the portal. Shares the token key with the Chat
// and Voicemail tabs ("sc-chat-token") so signing in anywhere signs in
// everywhere — same member identity as the softphone.

const API = "/api/v1/messaging";
export const TOKEN_KEY = "sc-chat-token";
export const MEMBER_KEY = "sc-chat-member";
export const SESSION_EXPIRED_EVENT = "sc-member-session-expired";

function readStored(key) {
  try {
    return window.localStorage.getItem(key) || window.sessionStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function writeStored(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode */
  }
}

export function isPartnerSession(token = getMemberToken()) {
  return token.startsWith("scpa.");
}

export function memberLandingPath(member) {
  return member?.delegatedAdmin && !member?.workspaceMember ? "/admin/team" : "/agent";
}

function removeStored(key) {
  for (const storage of ["localStorage", "sessionStorage"]) {
    try {
      window[storage].removeItem(key);
    } catch {
      /* One blocked storage must not prevent clearing the other. */
    }
  }
}

export function getMemberToken() {
  return readStored(TOKEN_KEY);
}

export function getStoredMember() {
  try {
    return JSON.parse(readStored(MEMBER_KEY) || "null");
  } catch {
    return null;
  }
}

export function saveMemberSession(token, member) {
  writeStored(TOKEN_KEY, token);
  writeStored(MEMBER_KEY, JSON.stringify(member || null));
}

export function clearMemberSession() {
  removeStored(TOKEN_KEY);
  removeStored(MEMBER_KEY);
}

export async function memberApi(path, options = {}) {
  const token = getMemberToken();
  const resp = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      "X-SC-Member-Token": token,
      ...(options.headers || {}),
    },
  });
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    // An old in-flight request must never clear a newer sign-in.
    if (resp.status === 401 && token && token === getMemberToken()) {
      clearMemberSession();
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, {
        detail: { partnerSessionExpired: isPartnerSession(token) },
      }));
    }
    const err = new Error(body.error || body.message || `Request failed (${resp.status})`);
    err.status = resp.status;
    throw err;
  }
  return body;
}

// Validate a remembered token and refresh the stored member identity.
export async function fetchMemberWhoami() {
  const token = getMemberToken();
  const body = await memberApi("/session");
  const member = body.member || null;
  if (member) {
    const merged = { ...(getStoredMember() || {}), ...member };
    if (token === getMemberToken()) writeStored(MEMBER_KEY, JSON.stringify(merged));
    return merged;
  }
  return member;
}

export async function loginMember(email, password) {
  const resp = await fetch(`${API}/session`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(body.error || body.message || "Sign-in failed");
  saveMemberSession(body.token, body.member);
  return body.member;
}

export async function logoutMember() {
  try {
    await memberApi("/session", { method: "DELETE" });
  } catch {
    /* token may already be dead — still clear locally */
  }
  clearMemberSession();
}
